<template>
  <!-- Loading Spinner for Edit Mode -->
  <div
    v-if="loading"
    class="col-span-6 sm:col-span-3 xl:col-span-2 flex flex-col justify-end items-center mt-8"
  >
    <LoadingIcon icon="three-dots" class="w-8 h-8" />
  </div>

  <form v-if="!loading" class="validate-form" @submit.prevent="handleSave">
    <div>
      <div class="intro-y flex flex-col sm:flex-row items-center mt-8">
        <BookmarkIcon />
        <h2 class="text-lg font-medium mr-auto">{{ pageTitle }}</h2>
        <div class="w-full sm:w-auto flex mt-4 sm:mt-0">
          <button type="submit" class="btn btn-primary shadow-md flex items-center">
            <SaveIcon class="w-4 h-4 mr-2" /> {{ $t('common.save') }}
          </button>
          <router-link
            :to="{ name: 'side-menu-services' }"
            tag="a"
            class="btn shadow-md flex items-center"
          >
            <ArrowLeftCircleIcon class="w-4 h-4 mr-2" />
            {{ $t('common.back') }}
          </router-link>
        </div>
      </div>
      <div class="pos intro-y grid grid-cols-12 gap-5 mt-5">
        <div class="col-span-12">
          <div class="intro-y box p-5">
            <!-- Language Tabs -->
            <div class="post intro-y overflow-hidden box mt-5">
              <div
                class="post__tabs nav nav-tabs flex-col sm:flex-row bg-gray-300 dark:bg-dark-2 text-gray-600"
                role="tablist"
              >
                <a
                  v-for="lang in languages"
                  :key="lang.code"
                  :id="`${lang.code}-tab`"
                  data-toggle="tab"
                  :data-target="`#${lang.code}`"
                  href="javascript:;"
                  class="flex-1 py-2 rounded-md text-center"
                  :class="{ active: lang.code === 'sk' }"
                  role="tab"
                >
                  <country-flag :country="lang.flag" size="small" />
                  {{ $t(lang.translationKey) }}
                </a>
              </div>
              <div class="tab-content">
                <!-- Language-specific Fields -->
                <div
                  v-for="lang in languages"
                  :key="lang.code"
                  class="tab-pane"
                  :class="{ active: lang.code === 'sk' }"
                  :id="lang.code"
                  role="tabpanel"
                >
                  <div class="mt-3">
                    <label
                      :for="`name${lang.capitalized}`"
                      class="form-label w-full flex flex-col sm:flex-row"
                    >
                      {{ $t('common.name') }}
                      <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                        {{ $t('form.required') }},
                        {{ $t('form.constraint.gte2ch') }}
                      </span>
                    </label>
                    <input
                      :id="`name${lang.capitalized}`"
                      v-model.trim="validate[`name${lang.capitalized}`].$model"
                      type="text"
                      :name="`name${lang.capitalized}`"
                      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                      :class="{
                        'border-theme-6': validate[`name${lang.capitalized}`].$error
                      }"
                      placeholder="My Place"
                    />
                    <label
                      :for="`description${lang.capitalized}`"
                      class="form-label w-full flex flex-col sm:flex-row mt-3"
                    >
                      {{ $t('common.description') }}
                    </label>
                    <textarea
                      :id="`description${lang.capitalized}`"
                      :name="`description${lang.capitalized}`"
                      class="intro-y form-control py-3 px-4 box pr-10 placeholder-theme-13"
                      v-model="descriptions[lang.code]"
                    >
                    </textarea>
                  </div>
                </div>
              </div>
            </div>

            <!-- Validation Errors -->
            <div class="mt-3">
              <template v-for="lang in languages" :key="lang.code">
                <template v-if="validate[`name${lang.capitalized}`].$error">
                  <div
                    v-for="(error, index) in validate[`name${lang.capitalized}`].$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </template>
            </div>

            <div class="mt-3">
              <ImageUploader ref="imageUploader" :defaultImage="service?.image" />
            </div>

            <!-- Reservation Strategy -->
            <div class="mt-3">
              <ReservationStrategySelect
                v-model:reservationStrategy="reservationStrategy"
              />
              <ul class="my-2 ml-5" style="list-style-type: disc">
                <li v-for="strategy in reservationStrategies" :key="strategy">
                  <strong>
                    {{ $t('reservationStrategy.' + strategy.replaceAll('-', '_')) }}
                  </strong>
                  -
                  {{
                    $t('reservationStrategy.description.' + strategy.replaceAll('-', '_'))
                  }}
                </li>
              </ul>
            </div>

            <!-- Time/Duration Fields -->
            <div v-if="!isRangeStrategy">
              <div class="mt-3">
                <label
                  for="timeBefore"
                  class="form-label w-full flex flex-col sm:flex-row"
                >
                  {{ $t('form.field.time_before') }}
                  <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                    {{ $t('form.required') }},
                    {{ $t('form.constraint.min0min') }}
                  </span>
                </label>
                <input
                  id="timeBefore"
                  v-model.number="validate.timeBefore.$model"
                  type="number"
                  class="intro-y form-control"
                  :class="{ 'border-theme-6': validate.timeBefore.$error }"
                />
                <template v-if="validate.timeBefore.$error">
                  <div
                    v-for="(error, index) in validate.timeBefore.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
              <div class="mt-3">
                <label for="duration" class="form-label w-full flex flex-col sm:flex-row">
                  {{ $t('form.field.duration') }}
                  <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                    {{ $t('form.required') }}, {{ $t('form.constraint.min1min') }}
                  </span>
                </label>
                <input
                  id="duration"
                  v-model.number="validate.duration.$model"
                  type="number"
                  class="intro-y form-control"
                  :class="{ 'border-theme-6': validate.duration.$error }"
                />
                <template v-if="validate.duration.$error">
                  <div
                    v-for="(error, index) in validate.duration.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
              <div class="mt-3">
                <label
                  for="timeAfter"
                  class="form-label w-full flex flex-col sm:flex-row"
                >
                  {{ $t('form.field.time_after') }}
                  <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                    {{ $t('form.required') }},
                    {{ $t('form.constraint.min0min') }}
                  </span>
                </label>
                <input
                  id="timeAfter"
                  v-model.number="validate.timeAfter.$model"
                  type="number"
                  class="intro-y form-control"
                  :class="{ 'border-theme-6': validate.timeAfter.$error }"
                />
                <template v-if="validate.timeAfter.$error">
                  <div
                    v-for="(error, index) in validate.timeAfter.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
            </div>

            <!-- Time Range Fields -->
            <div v-if="isRangeStrategy">
              <div class="mt-3">
                <label
                  for="timeRangeStart"
                  class="form-label w-full flex flex-col sm:flex-row"
                >
                  {{ $t('form.field.time_range_start') }}
                  <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                    {{ $t('form.required') }},
                    {{ $t('form.constraint.timeFormat') }}
                  </span>
                </label>
                <input
                  id="timeRangeStart"
                  v-model.trim="validate.timeRangeStart.$model"
                  type="text"
                  placeholder="08:00"
                  class="intro-y form-control"
                  :class="{ 'border-theme-6': validate.timeRangeStart.$error }"
                />
                <template v-if="validate.timeRangeStart.$error">
                  <div
                    v-for="(error, index) in validate.timeRangeStart.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
              <div class="mt-3">
                <label
                  for="timeRangeEnd"
                  class="form-label w-full flex flex-col sm:flex-row"
                >
                  {{ $t('form.field.time_range_end') }}
                  <span class="sm:ml-auto mt-1 sm:mt-0 text-xs text-gray-600">
                    {{ $t('form.required') }},
                    {{ $t('form.constraint.timeFormat') }}
                  </span>
                </label>
                <input
                  id="timeRangeEnd"
                  v-model.trim="validate.timeRangeEnd.$model"
                  type="text"
                  placeholder="20:00"
                  class="intro-y form-control"
                  :class="{ 'border-theme-6': validate.timeRangeEnd.$error }"
                />
                <template v-if="validate.timeRangeEnd.$error">
                  <div
                    v-for="(error, index) in validate.timeRangeEnd.$errors"
                    :key="index"
                    class="text-theme-6 mt-2"
                  >
                    {{ $t(error.$message) }}
                  </div>
                </template>
              </div>
            </div>

            <!-- Multiselect Components -->
            <div class="mt-3">
              <BranchMultiselect v-model:selectedBranches="selectedBranches" />
            </div>
            <div class="mt-3">
              <ResourceMultiselect v-model:selectedResources="selectedResources" />
            </div>
            <div class="mt-3">
              <EmployeeMultiselect v-model:selectedEmployees="selectedEmployees" />
            </div>
            <div class="mt-3">
              <BranchPlaceMultiselect
                v-model:selectedBranchPlaces="selectedBranchPlaces"
              />
            </div>

            <!-- Price Form -->
            <div class="mt-3">
              <PriceForm v-model:price="price" />
            </div>

            <!-- Toggles -->
            <div class="form-check flex-col items-start mt-3">
              <label for="visible" class="form-check-label ml-0 mb-2">
                {{ $t('common.visibility') }}
              </label>
              <input
                id="visible"
                v-model="formData.visible"
                class="form-check-switch"
                type="checkbox"
              />
            </div>
            <div class="form-check flex-col items-start mt-3">
              <label for="active" class="form-check-label ml-0 mb-2">
                {{ $t('common.active') }}
              </label>
              <input
                id="active"
                v-model="formData.active"
                class="form-check-switch"
                type="checkbox"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>

  <!-- Notification Templates -->
  <div id="success-notification-content" class="toastify-content hidden flex">
    <CheckCircleIcon class="text-theme-9" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ successMessage }}!</div>
    </div>
  </div>
  <div id="failed-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ failureMessage }}!</div>
      <div class="text-gray-600 mt-1">{{ errorNote }}</div>
    </div>
  </div>
  <div id="failed-load-notification-content" class="toastify-content hidden flex">
    <XCircleIcon class="text-theme-6" />
    <div class="ml-4 mr-4">
      <div class="font-medium">{{ $t('service.notLoaded') }}!</div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, toRaw, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { required, minLength, minValue, integer, helpers } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import Toastify from 'toastify-js'
import serviceApi from '@/services/services/service.api'
import empty from '@/utils/isEmpty'
import { RESERVATION_STRATEGIES } from '@/utils/reservationStrategy'

// Import all your components
import BranchMultiselect from '../../components/branches/BranchMultiselect.vue'
import BranchPlaceMultiselect from '../../components/branches/BranchPlaceMultiselect.vue'
import ReservationStrategySelect from '@/components/services/ReservationStrategySelect.vue'
import ImageUploader from '@/components/cdn/ImageUploader.vue'
import EmployeeMultiselect from '@/components/employees/EmployeeMultiselect.vue'
import ResourceMultiselect from '@/components/resources/ResourceMultiselect.vue'
import PriceForm from '@/components/price/PriceForm.vue'

// --- PROPS & EMITS ---
const props = defineProps({
  serviceId: {
    type: String,
    default: null
  }
})

// --- COMPOSABLES ---
const router = useRouter()
const { t } = useI18n()

// --- STATE MANAGEMENT ---
const isEditMode = computed(() => !!props.serviceId)
const loading = ref(isEditMode.value) // Only show loader in edit mode
const errorNote = ref('')
const service = ref(null) // To store fetched service data in edit mode
const imageUploader = ref(null) // Template ref for ImageUploader

// --- DYNAMIC CONTENT ---
const pageTitle = computed(() =>
  isEditMode.value ? t('service.edit') : t('service.add')
)
const successMessage = computed(() =>
  isEditMode.value ? t('service.edited') : t('service.created')
)
const failureMessage = computed(() =>
  isEditMode.value ? t('service.notEdited') : t('service.notCreated')
)

// --- FORM STATE ---
const languages = [
  { code: 'sk', capitalized: 'Sk', flag: 'sk', translationKey: 'common.language.sk_SK' },
  { code: 'en', capitalized: 'En', flag: 'gb', translationKey: 'common.language.en_UK' },
  { code: 'cs', capitalized: 'Cs', flag: 'cz', translationKey: 'common.language.cs_CZ' },
  { code: 'de', capitalized: 'De', flag: 'de', translationKey: 'common.language.de_DE' },
  { code: 'pl', capitalized: 'Pl', flag: 'pl', translationKey: 'common.language.pl_PL' }
]

const formData = reactive({
  nameSk: '',
  nameEn: '',
  nameCs: '',
  nameDe: '',
  namePl: '',
  timeBefore: 15,
  duration: 0,
  timeAfter: 15,
  timeRangeStart: '',
  timeRangeEnd: '',
  visible: false,
  active: false
})

const descriptions = reactive({
  sk: '',
  en: '',
  cs: '',
  de: '',
  pl: ''
})

const selectedBranches = ref([])
const selectedBranchPlaces = ref([])
const selectedResources = ref([])
const selectedEmployees = ref([])
const reservationStrategy = ref({ reservationStrategy: 'service-employee' })
const price = ref({
  selectedType: '',
  selectedUnit: '',
  amount: '',
  currency: 'EUR',
  priceUrl: '',
})

const isRangeStrategy = computed(() =>
  reservationStrategy.value.reservationStrategy.endsWith('range')
)
const reservationStrategies = RESERVATION_STRATEGIES

// --- VALIDATION ---
const timeValidator = helpers.regex(/^(2[0-3]:[0-5]?[0-9]|[0-1]?[0-9]:[0-5]?[0-9])$/)

// By making `rules` a computed property, Vuelidate will automatically
// apply the correct validation based on the selected reservation strategy.
const rules = computed(() => {
  const baseRules = {
    nameSk: {
      required: helpers.withMessage('form.validation.name_sk.required', required),
      minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
    },
    nameEn: {
      required: helpers.withMessage('form.validation.name_en.required', required),
      minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
    },
    nameCs: {
      required: helpers.withMessage('form.validation.name_cs.required', required),
      minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
    },
    nameDe: {
      required: helpers.withMessage('form.validation.name_de.required', required),
      minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
    },
    namePl: {
      required: helpers.withMessage('form.validation.name_pl.required', required),
      minLength: helpers.withMessage('form.validation.gte2ch', minLength(2))
    }
  }

  if (isRangeStrategy.value) {
    return {
      ...baseRules,
      timeRangeStart: {
        required: helpers.withMessage('form.validation.requiredField', required),
        timeValidator: helpers.withMessage('form.validation.timeValidator', timeValidator)
      },
      timeRangeEnd: {
        required: helpers.withMessage('form.validation.requiredField', required),
        timeValidator: helpers.withMessage('form.validation.timeValidator', timeValidator)
      }
    }
  } else {
    return {
      ...baseRules,
      timeBefore: {
        required: helpers.withMessage('form.validation.minutes0', required),
        integer,
        minValue: helpers.withMessage('form.validation.minutes0', minValue(0))
      },
      duration: {
        required: helpers.withMessage('form.validation.minutes1', required),
        integer,
        minValue: helpers.withMessage('form.validation.minutes1', minValue(1))
      },
      timeAfter: {
        required: helpers.withMessage('form.validation.minutes0', required),
        integer,
        minValue: helpers.withMessage('form.validation.minutes0', minValue(0))
      }
    }
  }
})

const validate = useVuelidate(rules, formData)

// --- METHODS ---
const showToast = (elementId) => {
  Toastify({
    node: cash(elementId).clone().removeClass('hidden')[0],
    duration: 5000,
    newWindow: true,
    close: true,
    gravity: 'top',
    position: 'right',
    stopOnFocus: true
  }).showToast()
}

const convertPriceData = (priceData) => {
  return {
    type: priceData.selectedType,
    unit: priceData.selectedUnit,
    amount:
      priceData.amount !== null
        ? (
            (parseFloat(priceData.amount.toString().replace(',', '.')) * 100) |
            0
          ).toString()
        : null,
    currency: priceData.currency,
    priceUrl: priceData.priceUrl,
  }
}

const isStrategyCorrectCheck = async () => {
  const checkResetvationStrategy = toRaw(reservationStrategy.value.reservationStrategy)
  if (
    checkResetvationStrategy === 'service-resource-date-range' ||
    checkResetvationStrategy === 'service-branch-place-date-range'
  ) {
    return true
  }
  return empty.getNullIfEmpty(formData.duration) > 0
}

const handleSave = async () => {
  const isFormCorrect = await validate.value.$validate()
  if (!isFormCorrect) {
    errorNote.value = t('form.checkData')
    nextTick(() => {
      showToast('#failed-notification-content')
    })
    return
  }
  const isStrategyCorrect = await isStrategyCorrectCheck()
  if (!isStrategyCorrect) {
    errorNote.value = t('form.validation.durationRequiredForStrategy')
    nextTick(() => {
      showToast('#failed-notification-content')
    })
    return
  }

  const imageUrl = imageUploader.value.getValue()
  const saveRequest = {
    name: {
      sk_SK: formData.nameSk,
      en_US: formData.nameEn,
      cs_CZ: formData.nameCs,
      de_DE: formData.nameDe,
      pl_PL: formData.namePl
    },
    description: {
      sk_SK: descriptions.sk,
      en_US: descriptions.en,
      cs_CZ: descriptions.cs,
      de_DE: descriptions.de,
      pl_PL: descriptions.pl
    },
    image: imageUrl ?? (isEditMode.value ? service.value.image : null),
    timeBefore: isRangeStrategy.value ? 0 : formData.timeBefore,
    duration: isRangeStrategy.value ? null : empty.getNullIfEmpty(formData.duration),
    timeAfter: isRangeStrategy.value ? 0 : formData.timeAfter,
    timeRangeStart: isRangeStrategy.value ? formData.timeRangeStart : null,
    timeRangeEnd: isRangeStrategy.value ? formData.timeRangeEnd : null,
    reservationStrategy: toRaw(reservationStrategy.value.reservationStrategy),
    branches: toRaw(selectedBranches.value.selectedBranches) ?? [],
    branchPlaces: toRaw(selectedBranchPlaces.value.selectedBranchPlaces) ?? [],
    resources: toRaw(selectedResources.value.selectedResources) ?? [],
    employees: toRaw(selectedEmployees.value.selectedEmployees) ?? [],
    price: convertPriceData(toRaw(price.value)),
    active: formData.active,
    visible: formData.visible
  }

  try {
    if (isEditMode.value) {
      await serviceApi.editService(props.serviceId, saveRequest)
    } else {
      await serviceApi.createService(saveRequest)
    }
    showToast('#success-notification-content')
    router.push({ name: 'side-menu-services' })
  } catch (error) {
    console.error('Failed to save service:', error)
    errorNote.value = error.response?.data?.message || t('common.error.unknown')
    showToast('#failed-notification-content')
  }
}

// --- LIFECYCLE HOOKS ---
onMounted(async () => {
  if (!isEditMode.value) {
    return
  }

  try {
    const response = await serviceApi.getDetail(props.serviceId)
    service.value = response.data

    // Populate form data from the fetched service
    formData.nameSk = service.value.name.sk_SK
    formData.nameEn = service.value.name.en_US
    formData.nameCs = service.value.name.cs_CZ
    formData.nameDe = service.value.name.de_DE
    formData.namePl = service.value.name.pl_PL

    descriptions.sk = service.value.description.sk_SK
    descriptions.en = service.value.description.en_US
    descriptions.cs = service.value.description.cs_CZ
    descriptions.de = service.value.description.de_DE
    descriptions.pl = service.value.description.pl_PL

    formData.timeBefore = service.value.timeBefore
    formData.duration = empty.isEmpty(service.value.duration) ? 0 : service.value.duration
    formData.timeAfter = service.value.timeAfter
    formData.timeRangeStart = service.value.timeRangeStart
    formData.timeRangeEnd = service.value.timeRangeEnd

    selectedBranches.value.selectedBranches = service.value.branches
    selectedResources.value.selectedResources = service.value.resources
    selectedEmployees.value.selectedEmployees = service.value.employees
    selectedBranchPlaces.value.selectedBranchPlaces = service.value.branchPlaces

    reservationStrategy.value = {
      reservationStrategy: service.value.reservationStrategy
    }

    formData.active = service.value.active
    formData.visible = service.value.visible

    if (service.value.price) {
      const amount = service.value.price.amount
      price.value = {
        selectedType: service.value.price.type,
        selectedUnit: service.value.price.unit,
        amount: amount === null ? '' : amount / 100,
        currency: 'EUR',
        priceUrl: service.value.price?.priceUrl,
      }
    }
  } catch (error) {
    console.error('Failed to load service details:', error)
    showToast('#failed-load-notification-content')
    router.push({ name: 'side-menu-services' })
  } finally {
    loading.value = false
  }
})
</script>
